/**
 * Matches the names of params that hold a list of ids (`id`, `ids`, `stationId`, `stationIds`,
 * `paymentMethodId`, ...). Only these arrays are sorted by `stableParams`.
 */
const ID_KEY = /^ids?$|Ids?$/;

const compare = (a: unknown, b: unknown) => {
  if (a === b) {
    return 0;
  }

  return (a as number | string) < (b as number | string) ? -1 : 1;
};

const stableValue = (key: string, value: unknown): unknown => {
  if (Array.isArray(value)) {
    const copy = value.map((item) => stableValue('', item));

    return ID_KEY.test(key) ? copy.sort(compare) : copy;
  }

  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return stableParams(value as Record<string, unknown>);
  }

  return value;
};

/**
 * Params of a query, in the form used for both the query key and the service call.
 *
 * What: returns a deep COPY of `params` with every `undefined` value removed and every array of
 * ids (keys matching `id`, `ids`, `...Id`, `...Ids`) sorted.
 * Why: React Query compares keys by value. `{ id: [2, 1] }` and `{ id: [1, 2] }` ask for the same
 * data, so they must share one key; `{ a: undefined }` and `{}` too. Copying means a caller that
 * later mutates its own params object cannot change a key that is already in the cache.
 * Constraint: only arrays under id-like keys are sorted, because other arrays (sort orders,
 * columns) can be order-sensitive. The sorted id order is also what goes to the server, which
 * treats an id list as a set.
 *
 * @param params The filters or params object of a query.
 * @returns A new object that is safe to put in a query key.
 */
export const stableParams = <T extends Record<string, unknown>>(params: T): T => {
  const copy: Record<string, unknown> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      copy[key] = stableValue(key, value);
    }
  });

  return copy as T;
};

/**
 * Payload of a query function: the value, or `null` when the backend sent none.
 *
 * What: returns `payload`, or `null` when it is `undefined` (or already `null`).
 * Why: React Query v4 treats an `undefined` result of a query function as an error ("Query data
 * cannot be undefined"): nothing is saved, the page refetches on every mount and logs the error
 * every time. The backend answers 200 `{}` (no `data` field) for an empty result, for example
 * activity metrics of a period with no sessions, so `response.data.data` is `undefined` there.
 * `null` is a valid result: it is saved and the page shows its empty state.
 * Constraint: EVERY query function that returns a response field must pass it through this helper
 * (see `rules/react-query.md`). Consumers must treat `null` like "no data": a destructuring
 * default (`const { data = [] } = useQuery(...)`) does NOT replace `null`, only `undefined`.
 *
 * @param payload The response field the query function returns (usually `response.data.data`).
 * @returns `payload`, or `null` when there is none.
 */
export const unwrapData = <T>(payload: T | null | undefined): T | null => payload ?? null;
